import {
  ConsoleLogger,
  DefaultDeviceController,
  LogLevel,
  VideoPreference,
  VideoPreferences,
  VideoSource,
  TargetDisplaySize,
  isVideoTransformDevice,
  AudioVideoFacade
} from 'amazon-chime-sdk-js';
import type { Dispatch } from 'redux';
import { Logger } from 'src/latest/src/services';
import { setVideoDevice as setVideoDeviceAction } from 'src/redux/actions/teleTherapyActions';

interface MetricsData {
  availableOutgoingBitrate: number;
  audioUpstreamJitterMs: number;
  audioPacketLossPercent: number;
}

const LOW_BANDWIDTH_THRESHOLD = 500000;
const HIGH_JITTER_THRESHOLD = 100;
const HIGH_PACKET_LOSS_THRESHOLD = 5;

export const checkLowBandwidth = (metrics: MetricsData) => {
  const lowAvailableBitrate = metrics.availableOutgoingBitrate < LOW_BANDWIDTH_THRESHOLD;
  const highJitter = metrics.audioUpstreamJitterMs > HIGH_JITTER_THRESHOLD;
  const highPacketLoss = metrics.audioPacketLossPercent > HIGH_PACKET_LOSS_THRESHOLD;

  if (lowAvailableBitrate) {
    const bitrateKbps = metrics.availableOutgoingBitrate / 1000;
    const bitrateMbps = bitrateKbps / 1000;

    Logger.warn(
      bitrateMbps >= 1
        ? `Low bandwidth detected: ${bitrateMbps.toFixed(2)} Mbps`
        : `Low bandwidth detected: ${bitrateKbps.toFixed(2)} kbps`,
    );
  }

  if (highJitter) {
    Logger.warn(`High jitter detected: ${metrics.audioUpstreamJitterMs} ms`);
  }

  if (highPacketLoss) {
    Logger.warn(`High packet loss detected: ${metrics.audioPacketLossPercent}%`);
  }

  return lowAvailableBitrate || highJitter || highPacketLoss;
};

type BackgroundBlurDevice = MediaDeviceInfo & {
  intrinsicDevice(): Promise<MediaDeviceInfo>;
};

export const toggleBackgroundBlur = async (
  blurEnabled: boolean,
  videoDevice: MediaDeviceInfo | BackgroundBlurDevice,
  createBackgroundBlurDevice: (device: MediaDeviceInfo) => Promise<BackgroundBlurDevice>,
  isBackgroundBlurSupported: boolean,
  isVideoEnabledFlag: boolean,
  isVideoEnabled: boolean,
  toggleVideo: () => Promise<void>,
  audioVideo: AudioVideoFacade,
  fromBlurUpdate?: boolean,
) => {
  try {
    let current = videoDevice;

    if (blurEnabled && isBackgroundBlurSupported) {
      current = await createBackgroundBlurDevice(videoDevice);
    } else {
      if (isVideoTransformDevice(current)) {
        current = await (current as BackgroundBlurDevice).intrinsicDevice();
      }
    }

    if (isVideoEnabledFlag === true && !isVideoEnabled && !fromBlurUpdate) {
      await toggleVideo();
    }

    // Start the video input device with the current device
    await audioVideo?.startLocalVideoTile();
    await audioVideo?.startVideoInput(current);
  } catch (error) {
    console.error('Failed to toggle Background Blur', error);
  }
};

export const handleSetVideoResolution = async (resolution: string, audioVideo: AudioVideoFacade) => {
  try {
    switch (resolution) {
      case '720p':
        await audioVideo?.chooseVideoInputQuality(1280, 720, 15);
        break;
      case '360p':
        await audioVideo?.chooseVideoInputQuality(640, 360, 15);
        break;
      case '480p':
        await audioVideo?.chooseVideoInputQuality(640, 480, 15);
        break;
      default:
        await audioVideo?.chooseVideoInputQuality(640, 540, 15);
    }
  } catch (error) {
    console.error('Error setting video resolution:', error);
  }
};

export const updateDownlinkPreference = (
  priorityBasedDownlinkPolicy: any,
  remoteVideoSources: VideoSource[],
  metricsRef: React.MutableRefObject<MetricsData>
) => {
  if (!priorityBasedDownlinkPolicy) return;

  const videoPreferences = VideoPreferences.prepare();

  remoteVideoSources.forEach((source, index) => {
    const isLowBandwidth = checkLowBandwidth(metricsRef.current);
    const priority = isLowBandwidth ? (index === 0 ? 1 : 3) : index === 0 ? 1 : 2;

    videoPreferences.add(
      new VideoPreference(
        source.attendee.attendeeId,
        priority,
        isLowBandwidth ? TargetDisplaySize.Low : TargetDisplaySize.High,
      ),
    );
  });

  priorityBasedDownlinkPolicy.chooseRemoteVideoSources(videoPreferences.build());
};

export const startMeeting = async (
  audioVideo: AudioVideoFacade,
  videoDevice: MediaDeviceInfo | null,
  micDevice: MediaDeviceInfo | null,
  isMicEnabled: boolean,
  muted: boolean,
  toggleMute: () => Promise<void>,
  dispatch: Dispatch,
  setVideoDevice: typeof setVideoDeviceAction,
) => {
  try {
    const logger = new ConsoleLogger('MeetingLogger', LogLevel.OFF);
    const deviceController = new DefaultDeviceController(logger);

    if (!audioVideo) {
      throw new Error('Audio/Video not initialized');
    }

    // List available audio and video input devices
    const audioInputs = await deviceController.listAudioInputDevices();
    const videoInputs = await deviceController.listVideoInputDevices();
    
    Logger.log('Audio Inputs:', audioInputs);
    Logger.log('Video Inputs:', videoInputs);

    // Select and start the video input device if available
    if (videoInputs.length > 0) {
      let selectedVideoDevice = null;

      if (videoDevice) {
        selectedVideoDevice = videoInputs.find(
          input => input.deviceId === videoDevice.deviceId,
        );
      }

      if (!selectedVideoDevice) {
        selectedVideoDevice = videoInputs[0];
        dispatch(setVideoDevice(selectedVideoDevice));
      }

      if (selectedVideoDevice) {
        await audioVideo?.stopVideoInput();
      }
    }

    // Select and start the audio input device if available
    if (audioInputs.length > 0 && micDevice) {
      const selectedAudioDevice = audioInputs.find(
        input => input.deviceId === micDevice.deviceId,
      );

      if (selectedAudioDevice) {
        if (isMicEnabled) {
          await audioVideo?.startAudioInput(selectedAudioDevice);
        } else {
          await audioVideo.stopAudioInput();

          if (!muted) {
            await toggleMute();
          }
        }
      }
    }

    return deviceController;
  } catch (error) {
    console.error('Error starting meeting:', error);
    throw error;
  }
};

export const toggleContentShareCustom = async (
  isLocalUserSharing: boolean,
  toggleContentShare: (stream?: MediaStream) => void
) => {
  try {
    if (!isLocalUserSharing) {
      const mediaStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });

      toggleContentShare(mediaStream);
    } else {
      toggleContentShare();
    }
  } catch (error) {
    console.error('Error sharing content:', error);
  }
};
